import { motion, useInView } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { TrendingUp, ArrowDownRight, ArrowUpRight } from "lucide-react";

const productivityData = [
  { m: "Q1", before: 100, after: 100 },
  { m: "Q2", before: 102, after: 118 },
  { m: "Q3", before: 99, after: 142 },
  { m: "Q4", before: 104, after: 168 },
  { m: "Q5", before: 101, after: 196 },
];

const costData = [
  { c: "Labor", saving: 22 },
  { c: "Inventory", saving: 31 },
  { c: "Logistics", saving: 28 },
  { c: "Waste", saving: 41 },
  { c: "Rework", saving: 36 },
];

function CountUp({ end, suffix = "", decimals = 0 }) {
  const [v, setV] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 1400;
    const startTs = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - startTs) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setV(start + (end - start) * eased);
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, end]);
  return <span ref={ref} className="tabular">{v.toFixed(decimals)}{suffix}</span>;
}

const KPIS = [
  { v: 38, s: "%", l: "Avg. operating cost reduction", trend: "down", color: "#10B981" },
  { v: 2.4, s: "x", l: "Productivity uplift", decimals: 1, trend: "up", color: "#10B981" },
  { v: 67, s: "%", l: "Faster cycle times", trend: "up", color: "#10B981" },
  { v: 99.2, s: "%", l: "Inventory accuracy", decimals: 1, trend: "up", color: "#10B981" },
];

export default function Results() {
  return (
    <section id="results" className="bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid md:grid-cols-12 gap-8 mb-14">
          <div className="md:col-span-7">
            <div className="section-eyebrow mb-5"><TrendingUp size={12} /> Outcomes</div>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0A2540] leading-tight">
              Delivering measurable results
            </h2>
          </div>
          <div className="md:col-span-5 flex items-end">
            <p className="text-slate-600 leading-relaxed">
              Real numbers from real engagements. Every Clinivon program ships with a baseline,
              targets, and a transparent before / after scorecard.
            </p>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {KPIS.map((k, i) => (
            <motion.div
              key={k.l}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.4 }}
              className="bg-[#F8FAFC] border border-slate-200 p-6 group hover:border-[#10B981] transition-colors"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">Avg.</span>
                {k.trend === "up" ? (
                  <ArrowUpRight size={16} className="text-[#10B981]" />
                ) : (
                  <ArrowDownRight size={16} className="text-[#10B981]" />
                )}
              </div>
              <div className="font-display font-bold text-4xl text-[#0A2540] leading-none">
                <CountUp end={k.v} suffix={k.s} decimals={k.decimals || 0} />
              </div>
              <div className="text-xs text-slate-500 mt-3 leading-relaxed">{k.l}</div>
            </motion.div>
          ))}
        </div>

        {/* Charts */}
        <div className="grid lg:grid-cols-5 gap-px bg-slate-200 border border-slate-200">
          <div className="bg-white p-6 lg:col-span-3">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">Performance Index</div>
                <h4 className="font-display font-semibold text-[#0A2540] text-lg">Productivity: before vs after</h4>
              </div>
              <div className="flex items-center gap-3 text-[10px] uppercase tracking-wider">
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 bg-slate-300" /> Before</span>
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 bg-[#10B981]" /> After</span>
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={productivityData} margin={{ top: 8, right: 12, bottom: 0, left: -10 }}>
                  <defs>
                    <linearGradient id="aft" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#10B981" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="m" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 4, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  <Area type="monotone" dataKey="before" stroke="#CBD5E1" fill="transparent" strokeWidth={1.5} strokeDasharray="4 4" />
                  <Area type="monotone" dataKey="after" stroke="#10B981" fill="url(#aft)" strokeWidth={2.5} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white p-6 lg:col-span-2">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500 mb-1">Cost Savings %</div>
            <h4 className="font-display font-semibold text-[#0A2540] text-lg mb-4">By value stream</h4>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={costData} margin={{ top: 8, right: 12, bottom: 0, left: -10 }}>
                  <CartesianGrid stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="c" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 4, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  <Bar dataKey="saving" fill="#0A2540" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
